import { EFilterConfigType, IFilterConfig } from 'components/filter/types';
import ToastContainer from 'components/toasts/container';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { Icon } from 'components/ui/icon';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { toIsoStringWithTz } from 'utils/date-format';
import { Banner } from 'components/ui/banner';
import DateExport from './date';
import ListExport from './list';
import NumberExport from './number';
import TipInAccurateDataBanner from 'views/tips/components/TipBanner';
import { tipsThresholdDate, EXPORT_LIMIT } from 'utils/constants';

export interface IExport {
  start_date?: string;
  end_date?: string;
  status?: string;
  store?: string;
  condition?: string;
  amount_1?: string;
  amount_2?: string;
  payment_type?: string;
  [arg: string]: string | undefined;
}

interface IExportResponse {
  url: string;
  total_rows?: number;
  exported_rows?: number;
  is_limited?: boolean;
}

interface IProps {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  config: IFilterConfig[]; // Will use filterconfig until export diverges from filter
  onExport: (arg: IExport) => void;
  heading: string;
  reportUrl: string;
  exportResponse?: IExportResponse;
  label: string;
  errorMessage?: string;
  changeKey?: string;
  changeKeyUpdateHandlerCb?: (arg: IFilterConfig[]) => IFilterConfig[]; // callback to update filter config based on changeKey updates
  toClearExport: () => void;
  isTipsExport?: boolean; // to render tips corrupt data info banner
}
const ExportModal = ({
  showModal,
  setShowModal,
  config,
  onExport,
  heading,
  reportUrl,
  exportResponse,
  label,
  errorMessage,
  toClearExport,
  changeKey,
  changeKeyUpdateHandlerCb,
  isTipsExport = false,
}: IProps): JSX.Element => {
  // default date values prefilled in input
  const defaultStartDate = toIsoStringWithTz(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const changeIndex = config.findIndex((i) => i.key === changeKey);

  const [amountError, setAmountError] = useState(false);

  const defaultEndDate = toIsoStringWithTz(new Date());
  const [startDate, setStartDate] = useState<string>(defaultStartDate);
  const [endDate, setEndDate] = useState<string>(defaultEndDate);

  const [showExportToast, setShowExportToast] = useState<boolean>(false);
  const successExportMessage = 'Your export is ready!';

  const [items, setItems] = useState(config);
  const updateDates = (item: IFilterConfig) => {
    if (item.type === EFilterConfigType.date && item.data.values?.length > 0) {
      setStartDate(item.data.values[0]);
      setEndDate(item.data.values[1]);
    }
  };

  const [submitExport, setSubmitExport] = useState(false);

  const toggle = () => {
    setShowModal(!showModal);
    setAmountError(false);
    items.forEach((item) => {
      if (item.key !== 'dates') {
        item.selected = false;
      }
    });
    setSubmitExport(false);
    toClearExport();
  };

  const downloadFile = (filePath: string) => {
    const link = document.createElement('a');
    link.href = filePath;

    link.download = `${label}-${startDate}-${endDate}` + '.csv';

    link.click();
    // Don't clear export response if limited - keep warning visible
    if (!exportResponse?.is_limited) {
      toClearExport();
    }
  };

  const exportCSV = () => {
    const params: IExport = {};
    let error = false;

    items.forEach((item) => {
      const { data } = item;
      const { values } = data;

      params.start_date = startDate;
      params.end_date = endDate;

      if (item.type === EFilterConfigType.number && item.selected) {
        [params.condition] = values;
        if (values[0] !== 'between' && values[1]) {
          params.amount_1 = values[1].toString();
        } else if (values[1] && values[2]) {
          params.amount_1 = values[1].toString();
          params.amount_2 = values[2].toString();
        } else {
          error = true;
        }
      }
      if (item.type === EFilterConfigType.list && item.selected) {
        if (values.length > 0) {
          params[item.key] = values.join();
        }
      }
    });
    setSubmitExport(true);
    setAmountError(error);
    if (!error) {
      onExport(params);
    }
  };

  useEffect(() => {
    if (changeKey && changeKeyUpdateHandlerCb) {
      setItems(changeKeyUpdateHandlerCb(items));
    }
  }, [items?.[changeIndex]?.data?.values?.length]);

  useEffect(() => {
    const urlToDownload = exportResponse?.url || reportUrl;
    if (urlToDownload) {
      // Don't auto-close modal if export was limited - let user see the warning
      if (!exportResponse?.is_limited) {
        setShowModal(false);
        setShowExportToast(true);
      }
      downloadFile(urlToDownload);
    }
  }, [reportUrl, exportResponse]);

  /*
   *  Set single value in an array to generate comma-separated values
   */
  const setValue = (key: string, index: number, value: string) => {
    const updatedItems = items.map((item) => {
      if (item.key === key) {
        item.data.values[index] = value;
        updateDates(item);
      }

      return item;
    });
    setItems(updatedItems);
  };

  const [filterCount, setFilterCount] = useState(0);
  const [filterName, setFilterName] = useState('');
  const [withInDateRange, setWithInDateRange] = useState(true);

  const getFilterDetails = () => {
    let count = 0;
    items.forEach((item) => {
      if (item.selected) {
        count += 1;
      }
      if (item.key === 'amount' && !item.selected) {
        setAmountError(false);
      }
    });
    setFilterCount(count);
  };

  useEffect(() => {
    if (filterCount === 1) {
      setFilterName(EFilterConfigType.date);
    } else {
      setFilterName(filterCount.toString());
    }
  }, [filterCount]);

  // set dates to default if not selected otherwise the values from config
  useEffect(() => {
    items.forEach((item) => {
      if (item.type === EFilterConfigType.date && !item.selected) {
        updateDates(item);
        item.selected = true;
        // Set default date if empty
        if (!item.data.values[0]) {
          setValue(item.key, 0, defaultStartDate);
        }
        if (!item.data.values[1]) {
          setValue(item.key, 1, defaultEndDate);
        }
      } else if (item.type === EFilterConfigType.date && item.selected) {
        updateDates(item);
      }
      return item;
    });
    getFilterDetails();
  }, [startDate, endDate, config]);

  /*
   *  Set entire array to generate comma-separated values
   */
  const setValues = (key: string, values: string[]) => {
    const updatedItems = items.map((item) => {
      if (item.key === key) {
        item.data.values = values;
      }
      return item;
    });
    setItems(updatedItems);
  };
  const setSelected = (key: string, value: boolean) => {
    const updatedItems = items.map((item) => {
      if (item.key === key) {
        item.selected = value;
      }
      return item;
    });
    setItems(updatedItems);
  };

  const getComponent = (item: IFilterConfig) => {
    switch (item.type) {
      case EFilterConfigType.date:
        return <DateExport setValue={setValue} item={item} />;
      case EFilterConfigType.number:
        return (
          <NumberExport
            setValue={setValue}
            item={item}
            setSelected={setSelected}
            setShowAmountError={setAmountError}
            amountError={amountError}
            setSubmitExport={setSubmitExport}
            submitExport={submitExport}
          />
        );
      case EFilterConfigType.list:
        return (
          <ListExport
            setValues={setValues}
            item={item}
            setSelected={setSelected}
          ></ListExport>
        );
      default:
        return <></>;
    }
  };

  useEffect(() => {
    if (isTipsExport) {
      if (new Date(startDate) < tipsThresholdDate) {
        setWithInDateRange(true);
      } else {
        setWithInDateRange(false);
      }
    }
  }, [startDate]);

  return (
    <>
      <Dialog
        open={showModal}
        onOpenChange={(open) => {
          if (!open) toggle();
        }}
      >
        <DialogContent
          // Don't let Radix move focus into the first date input on open —
          // react-datetime opens its calendar on focus, which made the picker
          // pop open (and look auto-triggered) every time the modal mounted.
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="flex max-h-[85vh] max-w-xl flex-col
            max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:top-0 max-md:h-full max-md:max-h-none max-md:w-full max-md:max-w-none max-md:translate-x-0 max-md:translate-y-0 max-md:rounded-none"
        >
          <DialogHeader>
            <DialogTitle>{heading}</DialogTitle>
          </DialogHeader>
          {/* Scrollable body: keeps the title and Export/Cancel footer pinned
              while a long list of banners + filters scrolls within the dialog. */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {amountError && (
              <Banner
                variant="error"
                message="To proceed with export, 1 change needs to be made"
                details="Please provide the amount to filter your results or uncheck the amount box"
                className="mb-4"
              />
            )}
            {errorMessage !== undefined && (
              <Banner
                variant="warning"
                message="There is no data to be exported based on your filter selection. Please readjust your filters and try again"
                showClose
                onClose={toClearExport}
                className="mb-4"
              />
            )}
            {exportResponse?.is_limited === true && (
              <Banner
                variant="warning"
                message="Export Limited"
                details={`Export limited to ${EXPORT_LIMIT.toLocaleString()} transactions. Exported ${
                  exportResponse?.exported_rows?.toLocaleString() || '0'
                } of ${
                  exportResponse?.total_rows?.toLocaleString() || '0'
                } total records. Please apply additional filters to narrow down your results.`}
                className="mb-4"
              />
            )}
            {isTipsExport && withInDateRange && (
              <TipInAccurateDataBanner isDismissable={false} />
            )}
            {items.length > 0 &&
              heading != 'Tip Summary' &&
              withInDateRange && (
                <div className="mb-3 flex">
                  <span
                    id={`settle-tooltip`}
                    className="mr-2 pl-1 text-gray-blue-50"
                  >
                    <Icon
                      name="info"
                      size={20}
                      className="text-muted-foreground"
                    />
                  </span>
                  <span className="text-sm font-medium text-gray-blue-80">
                    This export has {filterName}{' '}
                    {filterCount === 1 ? 'filter' : 'filters'} applied
                  </span>
                </div>
              )}
            {items.map((item) => getComponent(item))}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="ghost" onClick={toggle}>
              Cancel
            </Button>
            <Button variant="treez-green" onClick={exportCSV}>
              Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ToastContainer
        showToast={showExportToast}
        onClose={() => setShowExportToast(!showExportToast)}
        message={successExportMessage}
      />
    </>
  );
};

export default ExportModal;
